// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import { Session } from "@insite/client-framework/Services/SessionService";
import { QuoteModel, QuoteSettingsModel } from "@insite/client-framework/Types/ApiModels";
import { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { DataTableCell, DataTableRow, Link } from "@insite/mobius/index";
import React, { FC } from "react";
import { css } from "styled-components";

interface OwnProps {
    quote: QuoteModel;
    session: Session;
    quoteSettings: QuoteSettingsModel;
    extendedStyles?: QuoteSummaryCardStyles;
    // XNG-Changes: show/Hide columns
    showRequestedDate: boolean;
    showQuoteNumber: boolean;
    showPONumber: boolean;
    showStatus: boolean;
    showExpirationDate: boolean;
    showQuoteType: boolean;
    showCustomer: boolean;
    showShipToPickUp: boolean;
    // XNG-Changes 
    rfqQuoteDetailsPageUrl?: string;
}

type Props = OwnProps;

export interface QuoteSummaryCardStyles {
    quoteSummaryCardGridItem?: GridItemProps;
    container?: GridContainerProps;
    quoteDetailsLinkGridItem?: GridItemProps;
    typeGridItem?: GridItemProps;
    typeHeadingAndText?: SmallHeadingAndTextStyles;
    statusGridItem?: GridItemProps;
    statusHeadingAndText?: SmallHeadingAndTextStyles;
    orderDateGridItem?: GridItemProps;
    orderDateHeadingAndText?: SmallHeadingAndTextStyles;
    expirationDateGridItem?: GridItemProps;
    expirationDateHeadingAndText?: SmallHeadingAndTextStyles;
    salesRepGridItem?: GridItemProps;
    salesRepHeadingAndText?: SmallHeadingAndTextStyles;
    userNameGridItem?: GridItemProps;
    userNameHeadingAndText?: SmallHeadingAndTextStyles;
    customerGridItem?: GridItemProps;
    customerHeadingAndText?: SmallHeadingAndTextStyles;
    shipToAddressGridItem?: GridItemProps;
    shipToAddressHeadingAndText?: SmallHeadingAndTextStyles;
    // XNG-Changes
    dataTableRow?: DataTableRowProps;
    dataTableCell?: DataTableCellProps;
}

export const quoteSummaryCardStyles: QuoteSummaryCardStyles = {
    quoteSummaryCardGridItem: {
        width: 12,
        css: css`
            padding: 0 20px;
            max-width: 500px;
        `,
    },
    container: {
        gap: 5,
    },
    quoteDetailsLinkGridItem: { width: 12 },
    typeGridItem: { width: 6 },
    statusGridItem: { width: 6 },
    orderDateGridItem: { width: 6 },
    expirationDateGridItem: { width: 6 },
    salesRepGridItem: { width: 6 },
    userNameGridItem: { width: 6 },
    customerGridItem: { width: 6 },
    shipToAddressGridItem: { width: 6 },
};

const QuoteSummaryCard: FC<Props> = ({
    quote,
    session,
    quoteSettings,
    extendedStyles,
    showRequestedDate,
    showQuoteNumber,
    showPONumber,
    showStatus,
    showExpirationDate,
    showQuoteType,
    showCustomer,
    showShipToPickUp,
    // XNG-Changes     
    rfqQuoteDetailsPageUrl,
}) => {
    const [styles] = React.useState(() => mergeToNew(quoteSummaryCardStyles, extendedStyles));

    if (!quote) {
        return null;
    }

    // XNG-Changes: Change Strucuture to DataTable
    const {
        orderDate,
        orderNumber,
        poNumber,
        statusDisplay,
        expirationDate,
        typeDisplay,
        customerName,
        billTo,
        shipTo,
    } = quote;

    const orderDateDisplay = getLocalizedDateTime({
        dateTime: orderDate!,
        language: session.language,
        options: {
            year: "numeric",
            month: "numeric",
            day: "numeric",
        },
    });

    let expirationDateDisplay = "";
    if (expirationDate) {
        expirationDateDisplay = getLocalizedDateTime({
            dateTime: expirationDate,
            language: session.language,
            options: {
                year: "numeric",
                month: "numeric",
                day: "numeric",
            },
        });
    }

    return (
        <DataTableRow key={quote.id} {...styles.dataTableRow}>
            {showRequestedDate && <DataTableCell {...styles.dataTableCell}>{orderDateDisplay}</DataTableCell>}
            {showQuoteNumber && (
                <DataTableCell {...styles.dataTableCell}>
                    {/* XNG-Changes */}                    
                    <Link
                        href={`${rfqQuoteDetailsPageUrl}?quoteId=${quote.id}`}
                        data-test-selector="link"
                        aria-label={orderNumber}
                    >
                        {orderNumber}
                    </Link>
                </DataTableCell>
            )}
            {showPONumber && <DataTableCell {...styles.dataTableCell}>{poNumber}</DataTableCell>}
            {showStatus && <DataTableCell {...styles.dataTableCell}>{statusDisplay}</DataTableCell>}
            {showExpirationDate && <DataTableCell {...styles.dataTableCell}>{expirationDateDisplay}</DataTableCell>}
            {showQuoteType && quoteSettings.jobQuoteEnabled && (
                <DataTableCell {...styles.dataTableCell}>{typeDisplay}</DataTableCell>
            )}
            {showCustomer && <DataTableCell {...styles.dataTableCell}>{billTo?.companyName || ""}</DataTableCell>}
            {showShipToPickUp && <DataTableCell {...styles.dataTableCell}>{shipTo?.companyName || ""}</DataTableCell>}
        </DataTableRow>
    );
};

export default QuoteSummaryCard;
